import { arrayMove } from "@dnd-kit/sortable";

import {
  DocumentActionType,
  DocumentsAction,
} from "pages/DevelopWorkspace/contexts/DocumentsContext/actions.types";
import { CANCEL_RESULT_PLACEHOLDER } from "pages/DevelopWorkspace/contexts/DocumentsContext/constants";
import getNextDocumentWithFocusTabs from "pages/DevelopWorkspace/contexts/DocumentsContext/helpers/getNextDocumentOutputTab";
import {
  isQueryStatementCompleted,
  isQueryStatementDispatched,
} from "pages/DevelopWorkspace/contexts/DocumentsContext/helpers/getQueryStatementState";
import { isQueryStatementChangesValid } from "pages/DevelopWorkspace/contexts/DocumentsContext/helpers/isQueryStatementChangesValid";
import { normalizeState } from "pages/DevelopWorkspace/contexts/DocumentsContext/helpers/normalizeState";
import { shouldIgnoreQSUpdate } from "pages/DevelopWorkspace/contexts/DocumentsContext/helpers/shouldIgnoreQSUpdate";
import {
  CancellationStatus,
  DocumentAiStateInitialData,
  DocumentAiStateUpdate,
  DocumentEngineMonitoring,
  DocumentLayout,
  DocumentOutputTab,
  DocumentsState,
  Execution,
  ExecutionContext,
  QueryStatement,
  QueryStatementStatus,
  SortOrder,
  WorkspaceDocument,
} from "pages/DevelopWorkspace/workspace.types";

import {
  SwitchActiveOutputPayload,
  SwitchDocumentActiveQueryStatementPayload,
} from "./types";

const actionHandlers = {
  [DocumentActionType.SORT_DOCUMENTS]: (
    state: DocumentsState,
    payload: SortOrder
  ): DocumentsState => {
    return {
      ...state,
      documents: [
        ...state.documents.sort((a, b) => {
          return payload === SortOrder.NewestFirst
            ? b.createdAt - a.createdAt
            : a.createdAt - b.createdAt;
        }),
      ],
    };
  },
  [DocumentActionType.ADD_DOCUMENT]: (
    state: DocumentsState,
    payload: WorkspaceDocument
  ): DocumentsState => {
    return {
      ...state,
      documents: [...state.documents, payload],
      activeDocumentId: payload.id,
    };
  },
  [DocumentActionType.REMOVE_DOCUMENT]: (
    state: DocumentsState,
    payload: string
  ): DocumentsState => {
    const documentIdToRemove = payload;

    const documentToRemoveIndex = state.documents.findIndex(
      document => document.id === documentIdToRemove
    );

    const removingActiveDocument =
      documentIdToRemove === state.activeDocumentId;

    const getNewActiveDocumentId = () => {
      // choose next active document
      const nextActiveDocumentIndex =
        documentToRemoveIndex === 0 ? 1 : documentToRemoveIndex - 1;
      const nextActiveDocument = state.documents[nextActiveDocumentIndex];
      const nextActiveDocumentId = nextActiveDocument
        ? nextActiveDocument.id
        : null;

      return nextActiveDocumentId;
    };

    const nextDocuments = state.documents.filter(
      document => document.id !== documentIdToRemove
    );

    return {
      ...state,
      documents: nextDocuments,
      activeDocumentId: removingActiveDocument
        ? getNewActiveDocumentId()
        : state.activeDocumentId,
    };
  },
  [DocumentActionType.REMOVE_ALL_DOCUMENTS]: (
    state: DocumentsState,
    payload: {
      exceptDocumentIds: string[];
    }
  ): DocumentsState => {
    const nextDocuments = state.documents.filter(document => {
      return payload.exceptDocumentIds.includes(document.id);
    });

    const getNextActiveDocumentId = () => {
      const activeDocumentId = state.activeDocumentId;
      const activeDocumentInNext = nextDocuments.find(
        document => document.id === activeDocumentId
      );

      if (activeDocumentInNext) {
        // keep the same active document
        return activeDocumentId;
      }
      if (nextDocuments[0]) {
        // choose first document as active
        return nextDocuments[0].id;
      }
      return null;
    };

    return {
      ...state,
      documents: nextDocuments,
      activeDocumentId: getNextActiveDocumentId(),
    };
  },
  [DocumentActionType.SET_ACTIVE_DOCUMENT]: (
    state: DocumentsState,
    payload: string
  ): DocumentsState => {
    return {
      ...state,
      activeDocumentId: payload,
    };
  },
  [DocumentActionType.CHANGE_DOCUMENT_CONTEXT]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      context: Partial<ExecutionContext>;
    }
  ): DocumentsState => {
    const { documentId, context } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === documentId) {
          return {
            ...document,
            execution: document.execution
              ? {
                  ...document.execution,
                  // clear error when switching engine or database
                  documentExecutionError: null,
                }
              : null,
            context: {
              ...document.context,
              ...context,
            },
          };
        }

        return document;
      }),
    };
  },
  [DocumentActionType.INIT_DOCUMENT_EXECUTION]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      execution: Execution;
    }
  ): DocumentsState => {
    const { documentId: executionDocumentId, execution } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === executionDocumentId) {
          return {
            ...document,
            layout: {
              ...document.layout,
              activeOutputTab: DocumentOutputTab.Results,
            },
            execution,
          };
        }

        return document;
      }),
    };
  },
  [DocumentActionType.UPDATE_DOCUMENT_QUERY_STATEMENT]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      id: string;
      queryStatement: Partial<Omit<QueryStatement, "prevStatus">>;
    }
  ): DocumentsState => {
    const {
      documentId: queryStatementDocumentId,
      id,
      queryStatement: nextQueryStatement,
    } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === queryStatementDocumentId) {
          if (!document.execution) {
            return document;
          }

          const nextDocument = {
            ...document,
            execution: {
              ...document.execution,
              queryStatements: document.execution.queryStatements.map(
                queryStatement => {
                  if (queryStatement.id === id) {
                    if (
                      nextQueryStatement.status &&
                      shouldIgnoreQSUpdate(
                        queryStatement,
                        nextQueryStatement.status
                      )
                    ) {
                      return queryStatement;
                    }

                    if (
                      !isQueryStatementChangesValid(
                        queryStatement,
                        nextQueryStatement
                      )
                    ) {
                      return queryStatement;
                    }

                    const nextStatus = nextQueryStatement.status;
                    const currentStatus = queryStatement.status;

                    // Only update prevStatus if we're transitioning to a different non-null status
                    // and the new status is different from both current status and prevStatus
                    const shouldUpdatePrevStatus =
                      nextStatus &&
                      nextStatus !== currentStatus &&
                      nextStatus !== queryStatement.prevStatus;

                    const queryStatementState = {
                      ...queryStatement,
                      ...nextQueryStatement,
                      status: nextStatus || currentStatus,
                      prevStatus: shouldUpdatePrevStatus
                        ? currentStatus
                        : queryStatement.prevStatus,
                      // if new stats are empty, use the old ones (never erase the stats)
                      statistics: nextQueryStatement.statistics
                        ?.executionTimeSec
                        ? nextQueryStatement.statistics
                        : queryStatement.statistics,
                    };

                    return queryStatementState;
                  }

                  return queryStatement;
                }
              ),
            },
          };

          return getNextDocumentWithFocusTabs(nextDocument);
        }

        return document;
      }),
    };
  },
  [DocumentActionType.CANCEL_DOCUMENT_EXECUTION]: (
    state: DocumentsState,
    payload: string
  ): DocumentsState => {
    const documentIdToCancel = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === documentIdToCancel) {
          if (!document.execution) {
            return document;
          }

          const requireServerCancellation =
            document.context.engine.name !== "system";

          return {
            ...document,
            execution: {
              ...document.execution,
              cancellationStatus: requireServerCancellation
                ? CancellationStatus.Initiated
                : CancellationStatus.Cancelled,
              queryStatements: document.execution.queryStatements.map(
                queryStatement => {
                  if (
                    isQueryStatementDispatched(queryStatement) &&
                    requireServerCancellation
                  ) {
                    return queryStatement;
                  }

                  if (isQueryStatementCompleted(queryStatement)) {
                    return queryStatement;
                  }

                  return {
                    ...queryStatement,
                    result: {
                      ...CANCEL_RESULT_PLACEHOLDER,
                    },
                    status: QueryStatementStatus.cancelled,
                  };
                }
              ),
            },
          };
        }

        return document;
      }),
    };
  },
  [DocumentActionType.UPDATE_DOCUMENT_EXECUTION_CANCELLATION_STATUS]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      queryStatementId: string;
      cancellationStatus: CancellationStatus;
    }
  ): DocumentsState => {
    const {
      documentId: cancellationStatusDocumentId,
      queryStatementId,
      cancellationStatus,
    } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === cancellationStatusDocumentId) {
          if (!document.execution) {
            return document;
          }

          const queryStatement = document.execution.queryStatements.find(
            queryStatement => queryStatement.id === queryStatementId
          );

          if (!queryStatement) {
            // probably query statement belongs to previous execution session
            // don't update the execution object
            return document;
          }

          return {
            ...document,
            execution: {
              ...document.execution,
              cancellationStatus,
            },
          };
        }

        return document;
      }),
    };
  },

  [DocumentActionType.SWITCH_DOCUMENT_ACTIVE_QUERY_STATEMENT]: (
    state: DocumentsState,
    payload: SwitchDocumentActiveQueryStatementPayload
  ): DocumentsState => {
    const {
      documentId: activeQueryStatementDocumentId,
      queryStatementIndex,
      userSelectedActiveQueryStatementIndexTimestamp,
      activeOutputTab,
    } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === activeQueryStatementDocumentId) {
          if (!document.execution) {
            return document;
          }

          return {
            ...document,
            execution: {
              ...document.execution,
              userSelectedActiveQueryStatementIndexTimestamp,
              activeQueryStatementIndex: queryStatementIndex,
            },
            layout: {
              ...document.layout,
              activeOutputTab,
            },
          };
        }

        return document;
      }),
    };
  },

  [DocumentActionType.SWITCH_ACTIVE_OUTPUT_TAB]: (
    state: DocumentsState,
    payload: SwitchActiveOutputPayload
  ): DocumentsState => {
    const { documentId: activeOutputTabDocumentId, activeOutputTab } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === activeOutputTabDocumentId) {
          return {
            ...document,
            layout: {
              ...document.layout,
              activeOutputTab,
            },
          };
        }

        return document;
      }),
    };
  },

  [DocumentActionType.CHANGE_DOCUMENT_LAYOUT]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      layout: Partial<DocumentLayout>;
    }
  ): DocumentsState => {
    const { documentId: layoutDocumentId, layout } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === layoutDocumentId) {
          return {
            ...document,
            layout: {
              ...document.layout,
              ...layout,
            },
          };
        }

        return document;
      }),
    };
  },
  [DocumentActionType.SET_QUERY_STATEMENT_STATE]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      queryStatementIndex: number;
      data: Record<string, unknown>;
    }
  ): DocumentsState => {
    const { documentId, queryStatementIndex, data } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === documentId) {
          if (!document.execution) {
            return document;
          }
          return {
            ...document,
            execution: {
              ...document.execution,
              queryStatements: document.execution.queryStatements.map(
                (queryStatement, index) => {
                  if (index === queryStatementIndex) {
                    return {
                      ...queryStatement,
                      ...data,
                    };
                  }
                  return queryStatement;
                }
              ),
            },
          };
        }

        return document;
      }),
    };
  },
  [DocumentActionType.UPDATE_DOCUMENT_ENGINE_MONITORING]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      engineMonitoring: DocumentEngineMonitoring;
    }
  ): DocumentsState => {
    const { documentId: engineMonitoringDocumentId, engineMonitoring } =
      payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === engineMonitoringDocumentId) {
          return {
            ...document,
            engineMonitoring,
          };
        }

        return document;
      }),
    };
  },
  [DocumentActionType.REORDER_DOCUMENT]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      overDocumentId: string;
    }
  ): DocumentsState => {
    const { documentId, overDocumentId } = payload;
    const overDocumentIndex = state.documents.findIndex(
      document => document.id === overDocumentId
    );
    const documentIndex = state.documents.findIndex(
      document => document.id === documentId
    );

    // Return unchanged state if either document doesn't exist
    if (documentIndex === -1 || overDocumentIndex === -1) {
      return state;
    }

    const documents = arrayMove(
      state.documents,
      documentIndex,
      overDocumentIndex
    );

    return {
      ...state,
      documents,
    };
  },
  [DocumentActionType.ADD_DOCUMENT_AI_STATE]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      aiState: DocumentAiStateInitialData;
    }
  ): DocumentsState => {
    const { documentId, aiState } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === documentId) {
          if (document.aiState) {
            console.warn(
              "Trying to add AI state for document with existing AI state"
            );
            return document;
          }

          return {
            ...document,
            aiState: {
              ...aiState,
              aiResponse: null,
              isLoading: false,
              lastError: null,
            },
          };
        }
        return document;
      }),
    };
  },
  [DocumentActionType.REMOVE_DOCUMENT_AI_STATE]: (
    state: DocumentsState,
    payload: {
      documentId: string;
    }
  ): DocumentsState => {
    const { documentId } = payload;

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === documentId) {
          return {
            ...document,
            aiState: null,
          };
        }
        return document;
      }),
    };
  },
  [DocumentActionType.UPDATE_DOCUMENT_AI_STATE]: (
    state: DocumentsState,
    payload: {
      documentId: string;
      newAiState: DocumentAiStateUpdate;
    }
  ): DocumentsState => {
    const { documentId, newAiState } = payload;

    // Early exit if target document does not exist or has no AI state
    // Probably the document was removed
    const target = state.documents.find(d => d.id === documentId);
    if (!target || !target.aiState) {
      return state;
    }

    return {
      ...state,
      documents: state.documents.map(document => {
        if (document.id === documentId) {
          if (!document.aiState) {
            return document;
          }

          return {
            ...document,
            aiState: {
              ...document.aiState,
              ...newAiState,
              autoRun: false, // Important: prevent repeated triggering
            },
          };
        }
        return document;
      }),
    };
  },
};

const documentsReducer = (
  state: DocumentsState,
  action: DocumentsAction
): DocumentsState => {
  const { payload, type } = action;

  const handleAction = () => {
    // TODO switch/case remain for type resolution, find the way to do this
    switch (type) {
      case DocumentActionType.SORT_DOCUMENTS:
        return actionHandlers[type](state, payload);

      case DocumentActionType.ADD_DOCUMENT:
        return actionHandlers[type](state, payload);

      case DocumentActionType.REMOVE_DOCUMENT:
        return actionHandlers[type](state, payload);

      case DocumentActionType.REMOVE_ALL_DOCUMENTS:
        return actionHandlers[type](state, payload);

      case DocumentActionType.SET_ACTIVE_DOCUMENT:
        return actionHandlers[type](state, payload);

      case DocumentActionType.CHANGE_DOCUMENT_CONTEXT:
        return actionHandlers[type](state, payload);

      case DocumentActionType.INIT_DOCUMENT_EXECUTION:
        return actionHandlers[type](state, payload);

      case DocumentActionType.UPDATE_DOCUMENT_QUERY_STATEMENT:
        return actionHandlers[type](state, payload);

      case DocumentActionType.CANCEL_DOCUMENT_EXECUTION:
        return actionHandlers[type](state, payload);

      case DocumentActionType.UPDATE_DOCUMENT_EXECUTION_CANCELLATION_STATUS:
        return actionHandlers[type](state, payload);

      case DocumentActionType.SWITCH_DOCUMENT_ACTIVE_QUERY_STATEMENT:
        return actionHandlers[type](state, payload);

      case DocumentActionType.SWITCH_ACTIVE_OUTPUT_TAB:
        return actionHandlers[type](state, payload);

      case DocumentActionType.CHANGE_DOCUMENT_LAYOUT:
        return actionHandlers[type](state, payload);

      case DocumentActionType.UPDATE_DOCUMENT_ENGINE_MONITORING:
        return actionHandlers[type](state, payload);

      case DocumentActionType.REORDER_DOCUMENT:
        return actionHandlers[type](state, payload);

      case DocumentActionType.ADD_DOCUMENT_AI_STATE:
        return actionHandlers[type](state, payload);

      case DocumentActionType.REMOVE_DOCUMENT_AI_STATE:
        return actionHandlers[type](state, payload);

      case DocumentActionType.UPDATE_DOCUMENT_AI_STATE:
        return actionHandlers[type](state, payload);

      case DocumentActionType.SET_QUERY_STATEMENT_STATE:
        return actionHandlers[type](state, payload);

      default:
        return state;
    }
  };

  const newState = handleAction();

  return normalizeState(newState);
};
export { documentsReducer };
